import { useAuth0 } from '@auth0/auth0-react';
import { groupBy } from 'lodash';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Table, Skeleton, Tag, Flex, theme, Button, Dropdown, message, GetProp, TableProps, Input } from 'antd';
import { css } from '@emotion/css';
import { CreateCustomerModal } from './CreateCustomerModal';
import { useEffect, useState } from 'react';
import { EllipsisOutlined, CopyOutlined } from '@ant-design/icons';
import { useBackoffice } from '../backoffice/useBackoffice';
import dayjs from 'dayjs';
import { ClockIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RoutesTypes } from '../router/routerTypes';
import { CustomerRow, useCustomers } from './useCustomers';

const { Search } = Input;
const { Column } = Table;

type TablePaginationConfig = Exclude<GetProp<TableProps, 'pagination'>, boolean>;

interface TableParams {
  searchString?: string;
  pagination?: TablePaginationConfig;
}

const CustomersPage = () => {
  const navigate = useNavigate();
  const { developerId } = useBackoffice();
  const queryClient = useQueryClient();
  const [messageApi, contextHolder] = message.useMessage();
  const { token } = theme.useToken();
  const [newCustomerModalOpened, setNewCustomerModalOpened] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const customersQuery = useCustomers();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tableParams, setTableParams] = useState<TableParams>({
    searchString: searchParams.get('searchString') || '',
    pagination: {
      current: parseInt(searchParams.get('pageNumber') || '1'),
      pageSize: parseInt(searchParams.get('pageSize') || '10'),
    },
  });

  const createKYCSessionMutation = useMutation({
    mutationKey: ['create-customer-kyc-session'],
    mutationFn: async ({ customerId }: { customerId: string }) => {
      const token = await getAccessTokenSilently();
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/customers/${customerId}/kycb-session`,
        {},
        {
          params: {
            consumerId: developerId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return res.data?.data;
    },
  });

  const handleOpenCustomerModal = () => {
    setNewCustomerModalOpened(true);
  };

  const handleCloseCustomerModal = () => {
    setNewCustomerModalOpened(false);
  };

  const handleCopyKYCLink = (row: CustomerRow) => {
    if (createKYCSessionMutation.isPending) {
      return;
    }

    if (row?.kycs?.kyc_flow_link) {
      navigator.clipboard.writeText(row?.kycs?.kyc_flow_link);
      messageApi.success('KYC link copied to clipboard');
    } else {
      createKYCSessionMutation
        .mutateAsync({
          customerId: row.id,
        })
        .then((data) => {
          navigator.clipboard.writeText(data.kycs.kyc_flow_link);
          messageApi.success('KYC link copied to clipboard');
          return queryClient.invalidateQueries({
            queryKey: ['customers'],
          });
        })
        .catch(() => {
          messageApi.error('Can not copy KYC link');
        });
    }
  };

  const handleTableChange: TableProps<CustomerRow>['onChange'] = (pagination) => {
    setTableParams({
      pagination,
    });
  };

  const onSearch = (val: string) => {
    console.log(val);
    setTableParams({
      ...tableParams,
      searchString: val,
    });
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    if (typeof tableParams.searchString === 'string') {
      searchParams.set('searchString', String(tableParams.searchString));
      setSearchParams(searchParams, { replace: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableParams.searchString]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('pageSize', String(tableParams.pagination?.pageSize));
    searchParams.set('pageNumber', String(tableParams.pagination?.current));

    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableParams.pagination?.pageSize, tableParams.pagination?.current]);

  useEffect(() => {
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        total: customersQuery?.data?.totalCount,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customersQuery?.data?.totalCount]);

  if (customersQuery.isLoading) {
    return <Skeleton active />;
  }

  return (
    <Flex gap="middle" vertical>
      {contextHolder}
      <Flex align="center" justify="space-between" gap="middle">
        <Search
          defaultValue={tableParams.searchString}
          allowClear
          placeholder="Search by customer id, name or email..."
          onSearch={onSearch}
          style={{ width: 360 }}
        />

        <Button type="primary" onClick={handleOpenCustomerModal}>
          Create Customer
        </Button>
      </Flex>
      <Table<CustomerRow>
        onChange={handleTableChange}
        pagination={tableParams.pagination}
        loading={customersQuery.isFetching || createKYCSessionMutation.isPending}
        dataSource={customersQuery?.data?.items || []}
        onRow={(record) => ({
          onClick: (e) => {
            e.stopPropagation();
            navigate(
              `/${RoutesTypes.Customers}/${record.id}?developerId=${developerId}&pageSize=${
                tableParams.pagination?.pageSize
              }&pageNumber=${tableParams.pagination?.current}&searchString=${tableParams.searchString || ''}`
            );
          },
          style: { cursor: 'pointer' },
        })}
      >
        <Column
          title="Name"
          key="name"
          render={(_: any, record: CustomerRow) => {
            let name = `${record.first_name} ${record.last_name}`;
            if (record.company_name) {
              name = record.company_name;
            }

            const subHeaderStyles = css`
              color: ${token.colorTextSecondary};
              font-size: 12px;
            `;
            return (
              <Flex vertical>
                <span>{name}</span>
                <span className={subHeaderStyles}>{record.email}</span>
              </Flex>
            );
          }}
        />

        <Column
          title="Customer ID"
          key="name"
          render={(_: any, record: CustomerRow) => {
            const subHeaderStyles = css`
              color: ${token.colorTextSecondary};
            `;
            return (
              <Flex align="center">
                {record.id.substring(0, 12)}...
                <Button
                  type="text"
                  className={subHeaderStyles}
                  size="small"
                  icon={<CopyOutlined width={16} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(record.id);
                    messageApi.success('ID copied to clipboard');
                  }}
                />
              </Flex>
            );
          }}
        />

        <Column
          title="Type"
          key="type"
          render={(_: any, record: CustomerRow) => {
            return record.beneficiary_type[0].toUpperCase() + record.beneficiary_type.slice(1);
          }}
        />

        <Column
          title="Date Added"
          key="date_added"
          render={(_: any, record: CustomerRow) => {
            return dayjs(record.created_at).format('MMM D, YYYY');
          }}
        />

        <Column
          title="KYC Details"
          key="kyc_status"
          render={(_: any, record: CustomerRow) => {
            let badgeStyle = {
              backgroundColor: '#f7f8f9',
              color: '#333',
              border: '1px solid #eceef2',
              borderColor: '#eceef2',
              borderRadius: '6px',
            };
            let status = 'Not Started';

            const kycStatus = record?.kycs?.status;
            const subStatus = record?.kycs?.sub_status;
            const statusBreakdown = record?.kycs?.status_breakdown;

            if (
              statusBreakdown.some((b) => b.status === 'approved') &&
              !statusBreakdown.every((b) => b.status === 'approved')
            ) {
              return (
                <Flex gap={'small'} align="flex-start">
                  {Object.entries(groupBy(statusBreakdown, 'status')).map(([status, items]) => {
                    let badgeStyle = {
                      backgroundColor: '#f7f8f9',
                      color: '#333',
                      border: '1px solid #eceef2',
                      borderColor: '#eceef2',
                      borderRadius: '6px',
                    };
                    if (status === 'approved') {
                      badgeStyle.color = '#067857';
                      badgeStyle.backgroundColor = '#d1fae4';
                      badgeStyle.borderColor = '#aaf5d2';
                    } else {
                      badgeStyle.color = '#b5550d';
                      badgeStyle.backgroundColor = '#fef2c6';
                      badgeStyle.borderColor = '#fce489';
                    }

                    const groupedByCurrency = Object.entries(groupBy(items, 'currency'));
                    const currencyAndRails = groupedByCurrency.map(([currency, currencyItems], index) => (
                      <span>
                        {currency.toUpperCase()} ({currencyItems.map((c) => c.payment_rails).join(', ')})
                        {index + 1 === groupedByCurrency.length ? '' : ', '}
                      </span>
                    ));

                    return (
                      <>
                        <Tag style={badgeStyle}>
                          <Flex gap={'small'}>
                            {status === 'approved' ? <CheckIcon width={14} /> : <ClockIcon width={14} />}

                            <div>{currencyAndRails}</div>
                          </Flex>
                        </Tag>
                      </>
                    );
                  })}
                </Flex>
              );
            }

            if (kycStatus === 'approved') {
              badgeStyle.color = '#067857';
              badgeStyle.backgroundColor = '#d1fae4';
              badgeStyle.borderColor = '#aaf5d2';
              status = 'Approved';
            } else if (kycStatus === 'pending') {
              if (subStatus === 'kyc_form_submission_accepted') {
                badgeStyle.color = '#b5550d';
                badgeStyle.backgroundColor = '#fef2c6';
                badgeStyle.borderColor = '#fce489';
                status = 'Under Review';
              } else if (subStatus === 'kyc_form_resubmission_required') {
                badgeStyle.color = '#b5550d';
                badgeStyle.backgroundColor = '#fef2c6';
                badgeStyle.borderColor = '#fce489';
                status = 'Resubmission Required';
              } else {
                status = 'Pending KYC Completion';
              }
            } else if (kycStatus === 'rejected') {
              badgeStyle.color = '#bb1f1d';
              badgeStyle.backgroundColor = '#ffe4e3';
              badgeStyle.borderColor = '#fdc8c9';
              status = 'Rejected';
            }

            return <Tag style={badgeStyle}>{status}</Tag>;
          }}
        />

        <Column
          title="Actions"
          key="actions"
          render={(_: any, record: CustomerRow) => {
            return (
              <Dropdown
                trigger={['click']}
                menu={{
                  onClick: (info) => {
                    info.domEvent.stopPropagation();
                    if (info.key === 'copy_kyc_link') {
                      handleCopyKYCLink(record);
                    }
                  },
                  items: [
                    {
                      icon: <CopyOutlined />,
                      label: 'Copy KYC Link',
                      key: 'copy_kyc_link',
                    },
                  ],
                }}
              >
                <Button type="default" onClick={(e) => e.stopPropagation()} icon={<EllipsisOutlined />} />
              </Dropdown>
            );
          }}
        />
      </Table>

      <CreateCustomerModal open={newCustomerModalOpened} onClose={handleCloseCustomerModal} />
    </Flex>
  );
};

export default CustomersPage;
